import {Row, RowData, RowProperties} from 'notebook/tables/constants/RowConstants';
import {ColumnConstraint} from 'notebook/tables/constants/TableColumnConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import type {TableMutationMethods} from 'notebook/tables/store/logic/mutations/createTableMutations';
import {TableModel} from 'notebook/tables/store/models/TableModel';
import {createRowFillDownSelector} from 'notebook/tables/store/selectors/rowModel/RowFillDownSelectors';
import {createRowIdentitySelector} from 'notebook/tables/store/selectors/rowModel/RowIdentitySelectors';
import {createRowSelectionSelector} from 'notebook/tables/store/selectors/rowModel/RowSelectionSelectors';
import {createRowStatusSelector} from 'notebook/tables/store/selectors/rowModel/RowStatusSelectors';
import {
  createRowTableSelector,
  RowTableModel,
} from 'notebook/tables/store/selectors/rowModel/RowTableSelectors';
import {
  createDeepSelector,
  createTableStoreSelector,
} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';

/**
 * This holds volatile values of the model, or values that frequently changes due to evaluation or renders.
 * This should contain volatile values from the TableModel that are only relevant to this single row.
 *
 * Everything inside here will be deep-compare memoized.
 */
export interface VolatileRowModel {
  row: Row;
  evaluatedRow: Array<EvaluatedCell>;
  rowData: RowData | null;
  columnConstraints: Array<ColumnConstraint | null>;
}

/**
 * This model is what is passed down to the components, as well as to the CellModel.
 * This can contain data that is derived from the TableModel or TableEditableStore
 */
export interface RowModel extends VolatileRowModel {
  rowProperties: RowProperties;
  /**
   * True when this row is being rendered as part of the single-input view (see
   * {@link TableSingleInputGroup}). Row-level selectors use this to zero out
   * single-input-specific values (e.g. ghost cells, fill-down) instead of
   * branching inside `TableEditable._getTRProps`.
   */
  isSingleInput: boolean;

  /**
   * The active (focused) selected column index for this row, or null when the
   * active cell is not on this row.
   */
  selectedColIndexActive: number | null;
  /**
   * The minimum selected column index for this row, or null when the row is not
   * within the selection.
   */
  selectedColIndexMin: number | null;
  /**
   * The maximum selected column index for this row, or null when the row is not
   * within the selection.
   */
  selectedColIndexMax: number | null;
  /**
   * Whether this row's last selected cell should render the fill-down square.
   */
  hasFillDownSquare: boolean;

  /**
   * The minimum column index of the fill-down render box visible on this row, or
   * null when the row is outside the (edge-extended) render box.
   */
  fillMinJ: number | null;
  /**
   * The maximum column index of the fill-down render box visible on this row, or
   * null when the row is outside the (edge-extended) render box.
   */
  fillMaxJ: number | null;
  /** Whether this row renders the top edge of the fill-down box. */
  isFillDownBoxTopEdge: boolean;
  /** Whether this row renders the bottom edge of the fill-down box. */
  isFillDownBoxBottomEdge: boolean;
  /** Whether this row is inside the vertical span of the fill-down box. */
  isFilledDown: boolean;

  /**
   * Whether this row is the table's header row. Consumed by the row when rendering ghost cells;
   * per-cell header styling is derived on {@link CellModel.isHeaderCell}.
   */
  isHeaderRow: boolean;

  /**
   * The table-level values this row renders with, relayed from the {@link TableModel} so that
   * rendering a row only needs the row model. Cell-level selectors also read
   * {@link RowTableModel.isTableLocked} from here to derive whether an individual cell is locked.
   */
  table: RowTableModel;

  /** The table's shared mutation methods, relayed from {@link TableModel.mutations}. */
  mutations: TableMutationMethods;
}

function createVolatileRowModelSelector(
  rowIndex: number,
  tableSelector: (store: TableEditableStore) => TableModel
): (store: TableEditableStore) => VolatileRowModel {
  return createDeepSelector.withTypes<TableEditableStore>()(
    [
      // row
      (store) => store.volatileProps.table.table[rowIndex],
      // evaluatedRow
      (store) => store.volatileProps.computedTable[rowIndex],
      // rowData
      (store) => store.volatileProps.table.rowData?.[rowIndex] ?? null,
      // columnConstraints
      (store) => store.volatileProps.table.columnConstraints,
    ],
    (row, evaluatedRow, rowData, columnConstraints) => ({
      row,
      evaluatedRow,
      rowData,
      columnConstraints,
    })
  );
}

export function createRowModelSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  rowIndex: number,
  isSingleInput = false
): (store: TableEditableStore) => RowModel {
  return createTableStoreSelector(
    'rowModel',
    {
      volatileModel: createVolatileRowModelSelector(rowIndex, tableSelector),
      rowStatus: createRowStatusSelector(tableSelector, rowIndex),
      rowIdentity: createRowIdentitySelector(tableSelector, rowIndex),
      rowSelection: createRowSelectionSelector(tableSelector, rowIndex),
      rowFillDown: createRowFillDownSelector(tableSelector, rowIndex, isSingleInput),
      table: createRowTableSelector(tableSelector, isSingleInput),
      mutations: (store) => tableSelector(store).mutations,
    },
    ({volatileModel, rowStatus, rowIdentity, rowSelection, rowFillDown, table, mutations}): RowModel => ({
      ...volatileModel,
      // For now, leaving this named as rowProperties since this naming propagates through a lot of code
      // But this should be renamed because rowProperties is not specific
      rowProperties: rowStatus,
      isSingleInput,
      isHeaderRow: rowIdentity.isHeaderRow,
      selectedColIndexActive: rowSelection.selectedColIndexActive,
      selectedColIndexMin: rowSelection.selectedColIndexMin,
      selectedColIndexMax: rowSelection.selectedColIndexMax,
      hasFillDownSquare: rowSelection.hasFillDownSquare,
      fillMinJ: rowFillDown.fillMinJ,
      fillMaxJ: rowFillDown.fillMaxJ,
      isFillDownBoxTopEdge: rowFillDown.isFillDownBoxTopEdge,
      isFillDownBoxBottomEdge: rowFillDown.isFillDownBoxBottomEdge,
      isFilledDown: rowFillDown.isFilledDown,
      table,
      mutations,
    })
  );
}
